import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Check, Loader2 } from "lucide-react";
import { Reveal, Eyebrow } from "./Primitives";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ROLES = ["Oncology researcher", "Computational biologist", "Structural biologist", "Pharma / biotech", "Academic", "Other"];
const empty = { name: "", email: "", organization: "", role: "Computational biologist", message: "" };

const Field = ({ label, children }) => (
  <label className="block">
    <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-[var(--muted)]">{label}</span>
    {children}
  </label>
);
const input = "mt-2 w-full bg-transparent border-b border-[var(--line)] py-3 text-base text-[var(--text)] placeholder:text-[#8a9691] outline-none focus:border-[var(--teal)] transition-colors";

export const Waitlist = () => {
  const [f, setF] = useState(empty);
  const [state, setState] = useState("idle");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setState("loading");
    try {
      await axios.post(`${API}/waitlist`, f);
      setState("done");
      toast.success("Message sent. We'll get back to you soon.");
    } catch (err) {
      setState("idle");
      toast.error(err?.response?.status === 422 ? "Please check your name and email." : "Couldn't send right now. Please try again.");
    }
  };

  return (
    <section id="contact" data-testid="waitlist-section" className="relative py-24 md:py-32 overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_20%_80%,rgba(31,122,108,0.08),transparent_55%)]" />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-16">
        <Reveal>
          <Eyebrow>Get in touch</Eyebrow>
          <h2 className="font-display mt-5 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[0.95]">Bring us a structure you don't trust yet.</h2>
          <p className="mt-6 max-w-md text-[var(--muted)] leading-relaxed">The workbench is live and open to use. Want help with a family run, a report or an assisted brief — or have a protein, cohort or label problem you're stuck on? Send a message.</p>
        </Reveal>
        <Reveal delay={0.1}>
          {state === "done" ? (
            <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} data-testid="waitlist-success" className="h-full rounded-xl border border-[#1f7a6c]/40 bg-[var(--surface)] p-10 flex flex-col justify-center">
              <span className="grid h-12 w-12 place-items-center rounded-full bg-[var(--teal)] text-[var(--on-teal)]"><Check className="h-6 w-6" /></span>
              <h3 className="font-display mt-6 text-3xl font-bold">Thanks, {f.name.split(" ")[0]}.</h3>
              <p className="mt-3 text-[var(--muted)]">We've got your message and will reply at {f.email}.</p>
            </motion.div>
          ) : (
            <form onSubmit={submit} data-testid="waitlist-form" className="rounded-xl border border-[var(--line)] bg-[var(--surface)] p-6 sm:p-10 grid gap-7 shadow-[0_40px_100px_-50px_rgba(17,25,23,0.35)]">
              <div className="grid sm:grid-cols-2 gap-7">
                <Field label="Name"><input required value={f.name} onChange={set("name")} placeholder="Ada Lovelace" data-testid="waitlist-name-input" className={input} /></Field>
                <Field label="Email"><input required type="email" value={f.email} onChange={set("email")} placeholder="you@lab.org" data-testid="waitlist-email-input" className={input} /></Field>
              </div>
              <div className="grid sm:grid-cols-2 gap-7">
                <Field label="Organization"><input value={f.organization} onChange={set("organization")} placeholder="Institute or company" data-testid="waitlist-org-input" className={input} /></Field>
                <Field label="Role">
                  <select value={f.role} onChange={set("role")} data-testid="waitlist-role-select" className={`${input} appearance-none cursor-pointer`}>
                    {ROLES.map((r) => <option key={r} value={r} className="bg-[var(--surface)]">{r}</option>)}
                  </select>
                </Field>
              </div>
              <Field label="Message"><textarea rows={3} value={f.message} onChange={set("message")} placeholder="e.g. KRAS G12C cohort, label conflicts in our EGFR set…" data-testid="waitlist-message-input" className={`${input} resize-none`} /></Field>
              <button type="submit" disabled={state === "loading"} data-testid="waitlist-submit-btn"
                className="group mt-2 inline-flex items-center justify-center gap-2 rounded-md bg-[var(--teal)] px-7 py-4 font-semibold text-[var(--on-teal)] hover:bg-[var(--text)] disabled:opacity-60 transition-colors">
                {state === "loading" ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Send message
              </button>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
};
